import React from 'react';
import PropTypes from 'prop-types';
import { FaArrowDown, FaUserCheck, FaUserMinus, FaFilter } from 'react-icons/fa';
import './FunnelChart.css';

/**
 * Step-by-Step Conversion Funnel Chart.
 * Visualizes user progression, drop-off volumes, and conversion percentages across actions.
 */
export default function FunnelChart({
  funnelData,
  actionsMap = {},
  isLoading = false
}) {
  if (isLoading) {
    return (
      <div className="funnel-chart-loading">
        <div className="spinner" />
        <p>Calculating funnel conversions...</p>
      </div>
    );
  }

  const steps = funnelData?.steps || [];
  const totalJourneys = funnelData?.journeys || (steps[0]?.reached || 0);

  if (!steps || steps.length === 0) {
    return (
      <div className="funnel-chart-empty" data-testid="funnel-chart-empty">
        <FaFilter className="empty-funnel-icon" />
        <h4>No Funnel Steps Configured</h4>
        <p>Select two or more registered actions to compute conversion and drop-off rates.</p>
      </div>
    );
  }

  const maxReached = Math.max(1, ...steps.map((s) => s.reached || 0));

  return (
    <div className="funnel-chart-container" data-testid="funnel-chart-container">
      <div className="funnel-header-summary">
        <div className="summary-stat">
          <span className="summary-label">Total Sampled Journeys</span>
          <span className="summary-value">{totalJourneys.toLocaleString()}</span>
        </div>
        <div className="summary-stat">
          <span className="summary-label">Final Conversion</span>
          <span className="summary-value highlight">
            {steps[steps.length - 1]?.conversionPercent !== undefined
              ? `${Number(steps[steps.length - 1].conversionPercent).toFixed(1)}%`
              : 'N/A'}
          </span>
        </div>
      </div>

      <div className="funnel-steps-list">
        {steps.map((step, idx) => {
          const actionMeta = actionsMap[step.actionId] || {};
          const actionName = actionMeta.name || actionMeta.captureKey || `Action ${step.actionId.substring(0, 8)}...`;
          const reachedPercent = Math.max(8, (step.reached / maxReached) * 100);
          const isLast = idx === steps.length - 1;

          return (
            <React.Fragment key={step.actionId || idx}>
              <div className="funnel-step-card" data-testid={`funnel-step-${idx}`}>
                <div className="step-badge">Step {idx + 1}</div>

                <div className="step-info">
                  <div className="step-name-row">
                    <span className="step-name">{actionName}</span>
                    <span className="step-conversion-badge">
                      {step.conversionPercent !== undefined ? `${Number(step.conversionPercent).toFixed(1)}% Conversion` : ''}
                    </span>
                  </div>

                  {/* Horizontal Volume Bar */}
                  <div className="step-bar-track">
                    <div
                      className="step-bar-fill"
                      style={{ width: `${reachedPercent}%` }}
                    />
                    <span className="step-bar-label">
                      <FaUserCheck className="inline-icon" /> {step.reached.toLocaleString()} Reached
                    </span>
                  </div>
                </div>
              </div>

              {!isLast && (
                <div className="funnel-connector">
                  <div className="connector-line" />
                  <div className="connector-drop-badge">
                    <FaArrowDown className="connector-icon" />
                    <span>
                      <FaUserMinus className="inline-icon" /> {step.dropped?.toLocaleString() || 0} Dropped (
                      {step.reached > 0
                        ? `${(((step.dropped || 0) / step.reached) * 100).toFixed(1)}%`
                        : '0%'}
                      )
                    </span>
                  </div>
                  <div className="connector-line" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

FunnelChart.propTypes = {
  funnelData: PropTypes.shape({
    journeys: PropTypes.number,
    steps: PropTypes.arrayOf(PropTypes.shape({
      actionId: PropTypes.string.isRequired,
      reached: PropTypes.number.isRequired,
      dropped: PropTypes.number,
      conversionPercent: PropTypes.number
    }))
  }),
  actionsMap: PropTypes.object,
  isLoading: PropTypes.bool
};
